import { BASE_URL, CACHED_BASE_URL} from '../commonConfig/ApiConfig'

// Utility function to check if JWT token is expired
function isTokenExpired(token) {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    return true;
  }
}

async function apiRequest(endpoint, options = {}) {
  let token = localStorage.getItem('authToken');

  // List of public/auth endpoints that should NOT trigger session timeout logic
  const publicEndpoints = ['users/login', 'users/sign-up', 'users/refresh', 'users/forgot-password', 'users/reset-password'];
  const isPublicEndpoint = publicEndpoints.some(publicEndpoint => endpoint.includes(publicEndpoint));

  // Check if token is expired before making request (only for authenticated endpoints)
  if (!isPublicEndpoint && token && isTokenExpired(token)) {
    const refreshResult = await refreshToken();
    
    if (refreshResult && refreshResult.success) {
      token = localStorage.getItem('authToken');
    } else {
      return new Promise((resolve, reject) => {
        const showModalEvent = new CustomEvent('showSessionExtension', {
          detail: { resolve, reject, endpoint, options }
        });
        window.dispatchEvent(showModalEvent);
      });
    }
  }

  const isFormData = options.isFormData;
  
  const config = {
    headers: {
      // Only set Content-Type for non-FormData requests
      ...(!isFormData && { 'Content-Type': 'application/json' }),
      ...(token && { 'Authorization': `Bearer ${token}` }),
    },
    credentials: 'include',
    ...options,
  };

  delete config.isFormData;

  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, config);
    const result = await response.json();

    if (!response.ok) {
      if (!isPublicEndpoint && response.status === 403 && result.error === "Invalid or expired token") {
        const refreshResult = await refreshToken();
        
        if (refreshResult && refreshResult.success) {
          return apiRequest(endpoint, options);
        } else {
          return new Promise((resolve, reject) => {
            const showModalEvent = new CustomEvent('showSessionExtension', {
              detail: { resolve, reject, endpoint, options }
            });
            window.dispatchEvent(showModalEvent);
          });
        }
      }
      
      if (!isPublicEndpoint && (response.status === 401 || (response.status === 403 && result.error !== "Access denied. Admins only."))) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        window.dispatchEvent(new CustomEvent('sessionTimeout'));
      }
      
      const error = new Error(result.error || result.message || 'Something went wrong');
      error.status = response.status;
      error.data = result;
      throw error;
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection.');
    }
    throw error;
  }
}

export async function createWorkspace(workspaceData) {
  const wsName = workspaceData.ws_name || workspaceData.name;
  const wsData = workspaceData.ws_data || workspaceData;

  if (!wsName || wsName.trim() === '') {
    throw new Error('Workspace name is required and cannot be empty');
  }
  
  const payload = {
    ws_name: wsName,
    ws_data: typeof wsData === 'string' ? wsData : JSON.stringify(wsData),
    indicator_count: workspaceData.indicator_count || 0,
    location_count: workspaceData.location_count || 0
  };

  const response = await apiRequest('workspaces/create-workspace', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  return {
    data: {
      workspace: response.data || response.workspace || { 
        id: response.id || Date.now(), 
        ws_name: wsName,
        ws_data: wsData 
      }
    }
  };
}

export async function listWorkspaces() {
  const response = await apiRequest('workspaces/list-workspace', {
    method: 'GET',
  });

  let workspaces = response.workspaces || response.data?.workspaces || [];
  
  if (workspaces.length > 0 && !workspaces[0].ws_data) {
    const detailedWorkspaces = await Promise.all(
      workspaces.map(async (ws) => {
        try {
          const detailResponse = await getWorkspaceById(ws.id);
          if (detailResponse.data && detailResponse.data.workspace) {
            return detailResponse.data.workspace;
          }
          return ws; 
        } catch (error) {
          return ws; 
        }
      })
    );
    
    workspaces = detailedWorkspaces;
  }
  
  return {
    data: {
      workspaces: workspaces
    }
  };
}

export async function getWorkspaceById(workspaceId) {
  const response = await apiRequest(`workspaces/${workspaceId}`, {
    method: 'GET',
  });
  
  return {
    data: {
      workspace: response.workspace || response.data?.workspace || response
    }
  };
}

export async function updateWorkspace(workspaceId, workspaceData) {
  const response = await apiRequest(`workspaces/${workspaceId}`, {
    method: 'PUT',
    body: JSON.stringify(workspaceData),
  });
  
  return {
    data: {
      workspace: response.data || response.workspace || { 
        id: response.id || workspaceId, 
        ws_name: workspaceData.ws_name,
        ws_data: workspaceData.ws_data 
      }
    }
  };
}

export async function updateWorkspaceContent(workspaceId, workspaceData) {
  const payload = {
    ws_name: workspaceData.ws_name || workspaceData.name,
    ws_data: typeof workspaceData.ws_data === 'string' ? workspaceData.ws_data : JSON.stringify(workspaceData.ws_data),
    indicator_count: workspaceData.indicator_count || 0,
    location_count: workspaceData.location_count || 0
  };

  const response = await apiRequest(`workspaces/${workspaceId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  
  return {
    data: {
      workspace: response.data || response.workspace || { 
        id: response.id || workspaceId, 
        ws_name: payload.ws_name,
        ws_data: payload.ws_data 
      }
    }
  };
}

export async function deleteWorkspace(workspaceId) {
  return await apiRequest(`workspaces/${workspaceId}`, {
    method: 'DELETE',
  });
}

export async function deleteWorkspaces(workspaceIds) {
  return await apiRequest('workspaces/', {
    method: 'DELETE',
    body: JSON.stringify({ ids: workspaceIds }),
  });
}

export async function signUp(userData) {
  const result = await apiRequest("users/sign-up", {
    method: 'POST',
    body: JSON.stringify(userData),
  });
  return result;
}

export function clearDeviceId() {
  localStorage.removeItem('deviceId');
}

function generateCanvasFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const text = 'DeviceID-FP-' + Math.random();
    
    canvas.width = 200;
    canvas.height = 50;
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText(text, 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText(text, 4, 17);
    
    const dataURL = canvas.toDataURL();
    let hash = 0;
    for (let i = 0; i < dataURL.length; i++) {
      const char = dataURL.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  } catch (e) {
    return 'canvas-' + Math.random().toString(36).substring(2, 10);
  }
}

// WebGL fingerprinting helper
function getWebGLFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) return 'webgl-' + Math.random().toString(36).substring(2, 10);
    
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'unknown';
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown';
      return btoa(`${vendor}-${renderer}`).replace(/[^a-zA-Z0-9]/g, '').substring(0, 20);
    }
    
    return 'webgl-' + Math.random().toString(36).substring(2, 10);
  } catch (e) {
    return 'webgl-' + Math.random().toString(36).substring(2, 10);
  }
}

// Get connection info
function getConnectionInfo() {
  try {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      return `${connection.effectiveType || 'unknown'}-${connection.downlink || 0}`;
    }
    return 'conn-' + Math.random().toString(36).substring(2, 8);
  } catch (e) {
    return 'conn-' + Math.random().toString(36).substring(2, 8);
  }
}

// Generate or get device ID with GUARANTEED uniqueness
function getDeviceId() {
  let deviceId = localStorage.getItem('deviceId');
  
  if (!deviceId) {
    
    // STEP 1: Generate cryptographically secure random values (PRIMARY SOURCE OF UNIQUENESS)
    const cryptoArray = new Uint32Array(8);
    crypto.getRandomValues(cryptoArray);
    const cryptoRandom = Array.from(cryptoArray)
      .map(num => num.toString(36))
      .join('');
    
    // STEP 2: Add multiple timestamp-based randoms
    const timestamp = Date.now();
    const timestampStr = timestamp.toString(36);
    const random1 = Math.random().toString(36).substring(2, 15);
    const random2 = Math.random().toString(36).substring(2, 15);
    const random3 = Math.random().toString(36).substring(2, 15);
    const performanceRandom = (performance.now() * Math.random() * 1000000).toString(36);
    
    // STEP 3: Add another layer of crypto random
    const cryptoArray2 = new Uint32Array(4);
    crypto.getRandomValues(cryptoArray2);
    const cryptoRandom2 = Array.from(cryptoArray2)
      .map(num => num.toString(36))
      .join('');
    
    // STEP 4: Browser fingerprinting (SECONDARY - for additional entropy only)
    const canvas = generateCanvasFingerprint();
    const webgl = getWebGLFingerprint();
    const vendor = navigator.vendor || 'unknown';
    const platform = navigator.platform || 'unknown';
    const hardwareConcurrency = (navigator.hardwareConcurrency || 0).toString();
    const deviceMemory = (navigator.deviceMemory || 0).toString();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
    const connection = getConnectionInfo();
    
    // STEP 5: Build the unique string - RANDOM VALUES FIRST (most important for uniqueness)
    const uniqueString = [
      // PRIMARY: Pure random values (ensures uniqueness even on identical systems)
      cryptoRandom,
      timestampStr,
      random1,
      random2,
      random3,
      performanceRandom,
      cryptoRandom2,
      // SECONDARY: Browser fingerprint (additional entropy)
      canvas,
      webgl,
      vendor,
      platform,
      hardwareConcurrency,
      deviceMemory,
      timezone,
      connection,
      // Add one more random at the end
      Math.random().toString(36).substring(2)
    ].join('-');
    
    // STEP 6: Create device ID from the unique string
    // Use the full string without truncation to preserve uniqueness
    deviceId = btoa(uniqueString)
      .replace(/[^a-zA-Z0-9]/g, '')
      .substring(0, 64); // Take more characters for better uniqueness
    
    // STEP 7: Add final random suffix to absolutely guarantee uniqueness
    const finalCryptoArray = new Uint32Array(3);
    crypto.getRandomValues(finalCryptoArray);
    const finalRandom = Array.from(finalCryptoArray)
      .map(num => num.toString(36))
      .join('');
    
    deviceId = deviceId + finalRandom;
    
    // STEP 8: Store the device ID
    localStorage.setItem('deviceId', deviceId);
  }
  
  return deviceId;
}

export async function login(credentials) {

  const deviceId = getDeviceId();
  
  const loginData = {
    ...credentials,
    deviceId: deviceId
  };
  
  const result = await apiRequest("users/login", {
    method: 'POST',
    credentials: 'include', // Include cookies to receive refresh token cookie
    body: JSON.stringify(loginData),
  });
  
  return result;
}

export async function refreshToken() {
  try {
    const deviceId = getDeviceId();

    const response = await fetch(`${BASE_URL}users/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies in the request (refresh token is in HTTP-only cookie)
      body: JSON.stringify({ deviceId }), // Include device ID in body
    });

    const result = await response.json();

    if (response.ok && result.accessToken) {
      // Update only access token in localStorage (refresh token stays in HTTP-only cookie)
      localStorage.setItem('authToken', result.accessToken);
      
      if (result.user) {
        localStorage.setItem('userData', JSON.stringify(result.user));
        return { success: true, user: result.user };
      }
      
      try {
        const payload = JSON.parse(atob(result.accessToken.split('.')[1]));
        const userData = {
          id: payload.id,
          email: payload.email,
          is_admin: payload.is_admin
        };
        localStorage.setItem('userData', JSON.stringify(userData));
        return { success: true, user: userData };
      } catch (tokenError) {
        console.warn('Failed to parse user data from access token:', tokenError);
        return { success: true };
      }
    } else {
      // Refresh failed, clear tokens
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      return { success: false };
    }
  } catch (error) {
    // Refresh failed, clear tokens
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    return { success: false };
  }
}
// ==================== PALETTE API FUNCTIONS ====================

export async function createPalette(paletteData) {
  const response = await apiRequest('palettes', {  // Remove trailing slash
    method: 'POST',
    body: JSON.stringify({ palette_data: paletteData }),
  });

  return {
    data: {
      palette: response.data?.palette || response.palette
    }
  };
}

export async function listPalettes() {
  const response = await apiRequest('palettes', {  // Remove trailing slash
    method: 'GET',
  });

  return {
    data: {
      palettes: response.data?.palettes || response.palettes || []
    }
  };
}

export async function getPaletteById(paletteId) {
  const response = await apiRequest(`palettes/${paletteId}`, {
    method: 'GET',
  });
  
  return {
    data: {
      palette: response.data?.palette || response.palette
    }
  };
}

export async function updatePalette(paletteId, paletteData) {
  const response = await apiRequest(`palettes/${paletteId}`, {
    method: 'PUT',
    body: JSON.stringify({ palette_data: paletteData }),
  });
  
  return {
    data: {
      palette: response.data?.palette || response.palette
    }
  };
}

export async function deletePalette(paletteId) {
  return await apiRequest(`palettes/${paletteId}`, {
    method: 'DELETE',
  });
}

export async function updateUserProfile(userData) {
  const response = await apiRequest('users/edit', {
    method: 'POST',
    body: JSON.stringify(userData),
  }); 
  
  return {
    data: {
      user: response.data?.user || response.user || response
    }
  };
}

// Also add getCurrentUser function if not already present
export async function getCurrentUser() {
  const response = await apiRequest('users/me', {
    method: 'GET',
  });
  
  return {
    data: {
      user: response.data?.user || response.user || response
    }
  };
}
export async function logout() {
  try {
    const deviceId = getDeviceId();
    
    await fetch(`${BASE_URL}users/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies in the request (refresh token is in HTTP-only cookie)
      body: JSON.stringify({ deviceId }), // Include device ID in body
    });
    
  } catch (error) {
    // Even if logout API fails, we still want to clear local data
  } finally {
    // Always clear local storage
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
  }
}

export async function setDefaultWorkspace(workspaceId) {
  const response = await apiRequest(`workspaces/${workspaceId}/set-default`, {
    method: 'PUT',
  });
  
  return {
    data: {
      workspace: response.workspace || response.data?.workspace || response
    }
  };
}

export async function forgotPassword(email) {
  const response = await apiRequest('users/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  
  return {
    data: {
      message: response.message || response.data?.message || 'Reset link sent to email'
    }
  };
}

export async function resetPassword(token, newPassword) {
  const response = await apiRequest('users/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, newPassword }),
  });
  
  return {
    data: {
      message: response.message || response.data?.message || 'Password updated successfully',
      user: response.user || response.data?.user
    }
  };
}
export async function verifyEmail(user_id, email, token) {
  const response = await apiRequest('users/verify-email', {
    method: 'POST',
    body: JSON.stringify({ user_id, email, token }),
  });
  
  return {
    data: {
      message: response.message || response.data?.message || 'Email verified successfully!'
    }
  };
}
export async function submitScreening(screeningData) {
  try {
    const response = await fetch(`${BASE_URL}screening-info/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', 
      body: JSON.stringify({
        name: screeningData.name,
        designation: screeningData.designation,
        organisation_type: screeningData.organisation_type,
        organisation: screeningData.organisation,
        email: screeningData.email
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      const error = new Error(result.message || 'Failed to submit screening information');
      error.status = response.status;
      error.data = result;
      throw error;
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection.');
    }
    throw error;
  }
}
// export async function resendVerificationEmail(email) {
//   const response = await apiRequest('users/resend-verification', {
//     method: 'POST',
//     body: JSON.stringify({ email }),
//   });

// ==================== DEMO API FUNCTIONS ====================

export async function listDemos() {
  const response = await apiRequest('demo', {
    method: 'GET',
  });

  return {
    data: {
      demos: response.data?.demos || response.demos || []
    }
  };
}

export async function listDemosForAdmin() {
  const response = await apiRequest('demo/manage', {
    method: 'GET',
  });

  return {
    data: {
      demos: response.data?.demos || response.demos || []
    }
  };
}

export async function downloadDemo(demoId, demoType, demoTitle) {
  try {
    const token = localStorage.getItem('authToken');
    
    const response = await fetch(`${CACHED_BASE_URL}demo/download/${demoId}`, {
      method: 'GET',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const result = await response.json();
      throw new Error(result.message || 'Download failed');
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const result = await response.json();
      if (result.url) {
        window.open(result.url, '_blank');
        return { isYouTube: true };
      }
    }

 
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    let safeTitle = demoTitle
      .replace(/[^a-z0-9]/gi, "-")   
      .replace(/-+/g, "-")          
      .toLowerCase();

  
    let extension = demoType === "pdf" ? "pdf" : "mp4";


    let filename = `${safeTitle}.${extension}`;

    a.download = filename;

    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();

    return { isYouTube: false, success: true };

  } catch (error) {
    console.error("Download error:", error);
    throw error;
  }
}
export async function getAllUsers({ page = 1, limit = 10, search, from, to }) {
  const params = new URLSearchParams();
  params.append("page", page);
  params.append("limit", limit);

  if (search) params.append("search", search);
  if (from) params.append("from", from);
  if (to) params.append("to", to);

  const response = await apiRequest(`users?${params.toString()}`, {
    method: "GET",
  });
  
  return response; 
}

// ==================== SCREENING INFO API FUNCTIONS ====================

export async function getScreeningInfo({ page = 1, limit = 10, startDate, endDate }) {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('limit', limit);
  
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const response = await apiRequest(`screening-info?${params.toString()}`, {
    method: 'GET',
  });
  
  return response; // Returns { page, limit, total, data }
}



export async function downloadScreeningData({ startDate, endDate } = {}) {
  try {
    const token = localStorage.getItem('authToken');

    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const url = `${BASE_URL}screening-info/download?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const result = await response.json();
      throw new Error(result.message || 'Download failed');
    }

    // Convert to CSV blob and download
    const blob = await response.blob();
    const fileUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    const fileName =
      startDate || endDate
        ? `screening_filtered_${startDate || 'all'}_to_${endDate || 'all'}.csv`
        : `screening_all_${Date.now()}.csv`;

    a.href = fileUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(fileUrl);
    a.remove();

    return { success: true };
  } catch (error) {
    console.error("Download error:", error);
    throw error;
  }
}


export async function createDemo(formData) {
  return apiRequest('demo/manage', {
    method: 'POST',
    body: formData,
    isFormData: true,
  });
}

export async function updateDemo(id, formData) {
  return apiRequest(`demo/manage/${id}`, {
    method: 'PUT',
    body: formData,
    isFormData: true,
  });
}

export async function deleteDemo(id) {
  return apiRequest(`demo/manage/${id}`, {
    method: 'DELETE',
  });
}
export async function downloadAllUsers() {
  try {
    const token = localStorage.getItem("authToken");

    const url = `${BASE_URL}users/download`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      throw new Error("Download failed");
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = `users_all_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download error:", error);
    throw error;
  }
}
